import { useState, useEffect } from 'react';
import { fetchGallery, fetchVideos } from '@/lib/api';
import { Image, Video, X } from 'lucide-react';
import SEO from '@/components/SEO';

export default function Gallery() {
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);
  const [tab, setTab] = useState('photos');
  const [lightbox, setLightbox] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchGallery().then(setGallery).catch(() => {});
    fetchVideos().then(setVideos).catch(() => {});
  }, []);

  return (
    <div data-testid="gallery-page" className="pt-[68px]">
      <SEO title="Gallery" description="Browse photos and videos of Propel Sports NC — our facility, events, and sports in action." path="/gallery" />
      <section className="relative py-24 bg-[#0B1D3A] overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(ellipse 600px 400px at 50% 50%, rgba(242,101,34,0.25) 0%, transparent 70%)'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Gallery</h1>
          <p className="text-gray-300 text-lg">See our facility, events, and sports in action</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {/* Tabs */}
          <div className="flex justify-center gap-2 mb-10">
            <button
              data-testid="gallery-tab-photos"
              onClick={() => setTab('photos')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${
                tab === 'photos' ? 'bg-[#F26522] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Image size={16} /> Photos ({gallery.length})
            </button>
            <button
              data-testid="gallery-tab-videos"
              onClick={() => setTab('videos')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${
                tab === 'videos' ? 'bg-[#F26522] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Video size={16} /> Videos ({videos.length})
            </button>
          </div>

          {/* Photos */}
          {tab === 'photos' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.map(img => (
                <div
                  key={img.id}
                  data-testid={`gallery-image-${img.id}`}
                  onClick={() => setLightbox(img)}
                  className="relative rounded-2xl overflow-hidden aspect-video cursor-pointer group shadow-md"
                >
                  <img src={img.url} alt={img.caption} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform">
                    <div className="text-white text-sm font-medium">{img.caption}</div>
                    <div className="text-white/60 text-xs capitalize">{img.category}</div>
                  </div>
                </div>
              ))}
              {gallery.length === 0 && <p className="text-gray-400 col-span-3 text-center py-12">No photos yet</p>}
            </div>
          )}

          {/* Videos */}
          {tab === 'videos' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {videos.map(vid => (
                <div key={vid.id} data-testid={`gallery-video-${vid.id}`} className="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-md">
                  <video controls className="w-full aspect-video bg-black" preload="metadata">
                    <source src={vid.url} type="video/mp4" />
                  </video>
                  <div className="p-4">
                    <h3 className="text-[#0B1D3A] font-medium">{vid.title}</h3>
                    <p className="text-gray-500 text-sm">{vid.description}</p>
                  </div>
                </div>
              ))}
              {videos.length === 0 && <p className="text-gray-400 col-span-2 text-center py-12">No videos yet</p>}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button data-testid="lightbox-close" className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={() => setLightbox(null)}>
            <X size={28} />
          </button>
          <img src={lightbox.url} alt={lightbox.caption} className="max-w-full max-h-[85vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          <div className="absolute bottom-6 text-center">
            <div className="text-white font-medium">{lightbox.caption}</div>
          </div>
        </div>
      )}
    </div>
  );
}
